import { Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import { ReactComponent as MessagingCloseIcon } from "../../../common/icons/messaging-close.svg";
import { ReactComponent as NewMessageIcon } from "../../../common/icons/new-message.svg";
import AddParticipantDialog from "../AddParticipantDialog";
import ConversationSearchField from "../ConversationSearchField";
import { IN_PROGRESS_TAB_INDEX } from "../inboxTabs";
import MessagingInboxTabs from "../inbox/MessagingInboxTabs";
import { getMaximizeToolbarAction } from "../messagingViewModeActions";
import {
  selectIsFloatingWindowOpen,
  selectMessagingServiceUnavailable,
  setFloatingWindowOpen,
} from "../messagingSlice";
import MessagingToolbar from "../MessagingToolbar";
import { FloatingChatWindowBody, FloatingChatWindowContainer } from "../styles";
import useMessagingInboxSession from "../useMessagingInboxSession";
import useInboxConversationList from "../useInboxConversationList";
import useMessagingViewMode, {
  isMessagingPagePath,
} from "../useMessagingViewMode";
import FloatingChatPane from "./FloatingChatPane";

const closeIconSx = {
  fontSize: 18,
  color: "text.secondary",
  "& path": { stroke: "currentColor", fill: "none" },
} as const;

const FloatingChatWindow = () => {
  const { t } = useTranslation(["messaging", "common"]);
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isOpen = useAppSelector(selectIsFloatingWindowOpen);
  const serviceUnavailable = useAppSelector(selectMessagingServiceUnavailable);
  const isMessagingPage = isMessagingPagePath(pathname);
  const sessionEnabled = isOpen && !isMessagingPage && !serviceUnavailable;
  const { maximize } = useMessagingViewMode();
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] =
    useState(false);
  const { searching } = useInboxConversationList();

  const {
    tabIndex,
    handleTabChange,
    loadMore,
    selectedConversationId,
    selectedParticipant,
    onConversationChange,
    handleAddParticipant,
    clearSelectedConversation,
    allowedRecipientTypes,
  } = useMessagingInboxSession({ enabled: sessionEnabled });

  if (!sessionEnabled) return null;

  const isListView = !selectedConversationId && !selectedParticipant;
  const closeWindow = () => dispatch(setFloatingWindowOpen(false));

  return (
    <FloatingChatWindowContainer
      data-testid="floating-chat-window"
      elevation={8}
    >
      {isListView && (
        <Box sx={{ width: "100%", flexShrink: 0 }}>
          <MessagingToolbar
            compact
            inputComponent={<ConversationSearchField compact />}
            actions={[
              {
                onClick: () => setAddParticipantDialogOpen(true),
                icon: <NewMessageIcon />,
                ariaLabel: t("messaging:newMessage"),
              },
              getMaximizeToolbarAction({
                t: (key) => t(`messaging:${key}`),
                onClick: maximize,
                compact: true,
              }),
              {
                onClick: closeWindow,
                icon: (
                  <IconWrapper icon={MessagingCloseIcon} sx={closeIconSx} />
                ),
                ariaLabel: t("common:close"),
              },
            ]}
          />
          <MessagingInboxTabs
            value={searching ? false : tabIndex}
            handleTabChange={handleTabChange}
          />
        </Box>
      )}
      <FloatingChatWindowBody sx={{ padding: isListView ? undefined : 0 }}>
        <FloatingChatPane
          participant={selectedParticipant}
          loadMoreConversations={loadMore}
          onConversationChange={onConversationChange}
          onAssignedToMe={() =>
            handleTabChange(IN_PROGRESS_TAB_INDEX, "auto")
          }
          onBack={clearSelectedConversation}
        />
      </FloatingChatWindowBody>
      <AddParticipantDialog
        open={addParticipantDialogOpen}
        onClose={() => setAddParticipantDialogOpen(false)}
        allowedRecipientTypes={allowedRecipientTypes}
        onConfirm={(participant) => {
          setAddParticipantDialogOpen(false);
          handleAddParticipant(participant);
        }}
      />
    </FloatingChatWindowContainer>
  );
};

export default FloatingChatWindow;
